import * as React from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { PinIcon } from 'icons';
import { TimeZoneSelectOption } from 'types';
import { SupportedTimeZones } from 'enums';
import { useScreenWidth } from 'hooks';

interface TimeZoneAutocompleteProps {
  selectedTz: TimeZoneSelectOption | null;
  wrapperId: string;
  autocompleteOptions: TimeZoneSelectOption[];
  changeSelectedTz: (newValue: SupportedTimeZones | null) => void;
  error: string | null;
}

export const TimeZoneAutocomplete = ({
  selectedTz,
  autocompleteOptions,
  changeSelectedTz,
  error,
  wrapperId,
}: TimeZoneAutocompleteProps) => {
  const { isMobile } = useScreenWidth();
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <Box id={wrapperId}>
      <Typography
        variant="textMedium"
        color="text.secondary"
        mb={5}
        fontWeight={500}
        display="block"
      >
        Select preferred time zone
        {!isMobile && (
          <>
            <br />
            (or choose from the map)
          </>
        )}
      </Typography>

      <Autocomplete<TimeZoneSelectOption>
        sx={{ mb: 12 }}
        ListboxProps={{
          style: {
            maxHeight: '300px',
          },
        }}
        filterOptions={(options, { inputValue }) =>
          options.filter(({ label, searchContextHelper }) =>
            inputValue
              .trim()
              .toLowerCase()
              .split(' ')
              .every((word) =>
                `${label} ${searchContextHelper}`
                  .trim()
                  .toLowerCase()
                  .includes(word)
              )
          )
        }
        value={selectedTz ?? null}
        onChange={(event, newValue) => {
          changeSelectedTz(newValue?.id ?? null);

          // there's an IOS issue. Without this cursor will remain on the screen even if autocomplete is hidden
          if (isMobile) inputRef.current?.blur();
        }}
        options={autocompleteOptions}
        getOptionLabel={(option) => option.label ?? ''}
        isOptionEqualToValue={({ id }) => id === selectedTz?.id}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            helperText={error}
            error={!!error}
            InputProps={{
              ...params.InputProps,
              inputRef,
              startAdornment: (
                <InputAdornment position="start">
                  <PinIcon />
                </InputAdornment>
              ),
            }}
            placeholder="Location"
          />
        )}
      />
    </Box>
  );
};
